import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  CANCELLATION_REASON_LABELS,
  getReviewDetails,
  getReviewReasons,
} from '../constants/generatedCancellationFeedback';

const TAG_COLORS = {
  reviewed: { bg: '#e8f5e9', color: '#2e7d32' },
  important: { bg: '#fff3e0', color: '#e65100' },
  replied: { bg: '#e3f2fd', color: '#1565c0' },
};

export default function CancellationTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterReason, setFilterReason] = useState('all');
  const [filterSource, setFilterSource] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [stats, setStats] = useState({});

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'reviews'), orderBy('timestamp', 'desc')));
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setReviews(data);

      // compute reason stats
      const counts = {};
      data.forEach(r => {
        const reasons = getReviewReasons(r);
        if (reasons.length === 0) {
          counts.unknown = (counts.unknown || 0) + 1;
          return;
        }

        reasons.forEach(reason => {
          counts[reason] = (counts[reason] || 0) + 1;
        });
      });
      setStats(counts);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function toggleTag(id, tag) {
    const review = reviews.find(r => r.id === id);
    const tags = review.tags || [];
    const next = tags.includes(tag) ? tags.filter(t => t !== tag) : [...tags, tag];
    await updateDoc(doc(db, 'reviews', id), { tags: next });
    setReviews(prev => prev.map(r => r.id === id ? { ...r, tags: next } : r));
  }

  async function sendReply(review) {
    if (!replyText.trim() || !review.email) return;
    setReplying(true);
    try {
      const idToken = await auth_token();
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/api/admin/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ to: review.email, message: replyText, reviewId: review.id }),
      });
      if (!res.ok) throw new Error('Failed');
      await toggleTag(review.id, 'replied');
      setReplyText('');
      setExpanded(null);
      alert('Reply sent!');
    } catch (e) {
      alert('Failed to send reply. Check backend is running.');
    } finally {
      setReplying(false);
    }
  }

  async function auth_token() {
    const { getAuth } = await import('firebase/auth');
    return getAuth().currentUser?.getIdToken();
  }

  const reasonOptions = [...new Set(reviews.flatMap(getReviewReasons))];

  const filtered = reviews.filter(r => {
    const reasons = getReviewReasons(r);
    const details = getReviewDetails(r);
    const text = [r.email, details, ...reasons].join(' ').toLowerCase();
    if (search && !text.includes(search.toLowerCase())) return false;
    if (filterSource !== 'all' && r.source !== filterSource && !(filterSource === 'in_app' && !r.source)) return false;
    if (filterReason !== 'all') {
      if (!reasons.includes(filterReason)) return false;
    }
    return true;
  });

  const topReasons = Object.entries(stats).sort((a, b) => b[1] - a[1]).slice(0, 5);

  return (
    <div>
      <div style={s.pageHeader}>
        <h2 style={s.pageTitle}>Cancellation Feedback</h2>
        <p style={s.pageSubtitle}>{reviews.length} total responses</p>
      </div>

      {topReasons.length > 0 && (
        <div style={s.statsRow}>
          {topReasons.map(([reason, count]) => (
            <div key={reason} style={s.statCard}>
              <div style={s.statCount}>{count}</div>
              <div style={s.statLabel}>{CANCELLATION_REASON_LABELS[reason] || reason}</div>
            </div>
          ))}
        </div>
      )}

      <div style={s.filters}>
        <input style={s.searchInput} placeholder="Search feedback..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={s.select} value={filterSource} onChange={e => setFilterSource(e.target.value)}>
          <option value="all">All sources</option>
          <option value="in_app">In-app</option>
          <option value="web_form">Web form</option>
        </select>
        <select style={s.select} value={filterReason} onChange={e => setFilterReason(e.target.value)}>
          <option value="all">All reasons</option>
          {reasonOptions.map(r => <option key={r} value={r}>{CANCELLATION_REASON_LABELS[r] || r}</option>)}
        </select>
        <button style={s.refreshBtn} onClick={load}>↺ Refresh</button>
      </div>

      {loading ? (
        <div style={s.loading}>Loading feedback...</div>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>No feedback found.</div>
      ) : (
        <div style={s.list}>
          {filtered.map(r => {
            const reasons = getReviewReasons(r);
            const details = getReviewDetails(r);
            const tags = r.tags || [];
            const isOpen = expanded === r.id;
            const ts = r.timestamp?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) || '';

            return (
              <div key={r.id} style={s.card}>
                <div style={s.cardTop}>
                  <div style={s.cardMeta}>
                    <span style={s.email}>{r.email || 'Anonymous'}</span>
                    <span style={s.date}>{ts}</span>
                    <span style={{ ...s.sourceBadge, ...(r.source === 'web_form' ? s.webBadge : s.appBadge) }}>
                      {r.source === 'web_form' ? 'Web' : 'In-app'}
                    </span>
                  </div>
                  <div style={s.tagRow}>
                    {['reviewed', 'important', 'replied'].map(tag => (
                      <button
                        key={tag}
                        style={{ ...s.tag, ...(tags.includes(tag) ? TAG_COLORS[tag] : s.tagInactive) }}
                        onClick={() => toggleTag(r.id, tag)}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={s.reasonChips}>
                  {reasons.map(reason => (
                    <span key={reason} style={s.reasonChip}>{CANCELLATION_REASON_LABELS[reason] || reason}</span>
                  ))}
                </div>

                {details && (
                  <p style={s.feedbackText}>
                    {details}
                  </p>
                )}

                {r.email && (
                  <button style={s.replyToggle} onClick={() => setExpanded(isOpen ? null : r.id)}>
                    {isOpen ? 'Cancel reply' : '↩ Reply'}
                  </button>
                )}

                {isOpen && (
                  <div style={s.replyBox}>
                    <textarea
                      style={s.replyTextarea}
                      placeholder={`Reply to ${r.email}...`}
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      rows={4}
                    />
                    <button style={s.sendBtn} disabled={replying || !replyText.trim()} onClick={() => sendReply(r)}>
                      {replying ? 'Sending...' : 'Send from contact@thecura.app'}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const s = {
  pageHeader: { marginBottom: 24 },
  pageTitle: { fontSize: 24, fontWeight: 800, color: 'var(--green-900)' },
  pageSubtitle: { fontSize: 14, color: 'var(--text-muted)', marginTop: 4 },
  statsRow: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 },
  statCard: {
    background: 'white', borderRadius: 'var(--radius-md)', padding: '16px 20px',
    boxShadow: 'var(--shadow-sm)', minWidth: 120, flex: '1 1 120px',
  },
  statCount: { fontSize: 28, fontWeight: 800, color: 'var(--green-800)' },
  statLabel: { fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, fontWeight: 600 },
  filters: { display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' },
  searchInput: {
    flex: 2, minWidth: 180, padding: '10px 14px', borderRadius: 'var(--radius-sm)',
    border: '1.5px solid var(--border)', background: 'white', fontSize: 14, color: 'var(--text-primary)',
  },
  select: {
    flex: 1, minWidth: 130, padding: '10px 12px', borderRadius: 'var(--radius-sm)',
    border: '1.5px solid var(--border)', background: 'white', fontSize: 14, color: 'var(--text-primary)',
  },
  refreshBtn: {
    padding: '10px 16px', background: 'var(--green-100)', color: 'var(--green-800)',
    borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 700,
  },
  loading: { textAlign: 'center', padding: 48, color: 'var(--text-muted)' },
  empty: { textAlign: 'center', padding: 48, color: 'var(--text-muted)' },
  list: { display: 'flex', flexDirection: 'column', gap: 12 },
  card: {
    background: 'white', borderRadius: 'var(--radius-md)', padding: '20px',
    boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border)',
  },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, flexWrap: 'wrap', gap: 8 },
  cardMeta: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  email: { fontSize: 14, fontWeight: 700, color: 'var(--green-900)' },
  date: { fontSize: 12, color: 'var(--text-muted)' },
  sourceBadge: { fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999 },
  appBadge: { background: 'var(--green-100)', color: 'var(--green-800)' },
  webBadge: { background: '#e3f2fd', color: '#1565c0' },
  tagRow: { display: 'flex', gap: 6 },
  tag: { fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999, border: '1.5px solid transparent', cursor: 'pointer' },
  tagInactive: { background: 'var(--green-50)', color: 'var(--text-muted)', border: '1.5px solid var(--border)' },
  reasonChips: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 },
  reasonChip: {
    fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 999,
    background: 'var(--green-100)', color: 'var(--green-800)',
  },
  feedbackText: { fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 },
  replyToggle: {
    marginTop: 14, padding: '7px 14px', background: 'var(--green-50)',
    color: 'var(--green-800)', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 700,
  },
  replyBox: { marginTop: 12 },
  replyTextarea: {
    width: '100%', padding: '12px 14px', background: 'var(--green-50)',
    border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)',
    fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.6, resize: 'vertical', marginBottom: 10,
  },
  sendBtn: {
    padding: '10px 20px', background: 'linear-gradient(135deg, var(--green-800), var(--green-600))',
    color: 'white', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 700,
  },
};
